import { useState } from "react";
import { Check, ChevronLeft, ChevronRight, HelpCircle, Info, Save, X, BookOpen } from "lucide-react";

/**
 * WizardShell — TurboTax-style guided wizard wrapper.
 * Provides step sidebar, health bar, context help panel, and action bar.
 * 
 * Props:
 *   title        - Module name (e.g. "BIA Configuration Wizard")
 *   steps        - [{title, subtitle, isComplete}]
 *   currentStep  - Active step index (0-based)
 *   onStepChange - (stepIndex) => void
 *   completionPct - 0-100
 *   contextHelp  - {title, sections: [{heading, content}], isoClause?}
 *   onSave       - Save & Exit callback
 *   onBack       - Back step callback (null to hide)
 *   onContinue   - Continue/Next step callback
 *   continueLabel - Custom label for continue button
 *   autoSaved    - Show auto-save indicator
 *   children     - Form content
 */
export default function WizardShell({
  title = "Wizard",
  steps = [],
  currentStep = 0,
  onStepChange,
  completionPct = 0,
  contextHelp,
  onSave,
  onBack,
  onContinue,
  continueLabel = "Continue",
  autoSaved = true,
  children,
}) {
  const [helpOpen, setHelpOpen] = useState(true);

  return (
    <div className="flex flex-col h-full min-h-[calc(100vh-64px)] -m-6" data-testid="wizard-shell">
      {/* ── Health Bar ── */}
      <div className="bg-white border-b border-slate-200 px-6" data-testid="wizard-health-bar">
        <div className="flex items-center gap-4 py-3 max-w-full">
          <span className="text-xs font-semibold text-slate-500 whitespace-nowrap uppercase tracking-wider">{title}</span>
          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-sky-700 to-teal-500 transition-all duration-700"
              style={{ width: `${completionPct}%` }}
            />
          </div>
          <span className="text-sm font-bold text-sky-700 min-w-[36px] text-right" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {completionPct}%
          </span>
          {autoSaved && (
            <div className="flex items-center gap-1.5 text-xs text-teal-600 ml-2">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
              Auto-saved
            </div>
          )}
          {onSave && (
            <button
              onClick={onSave}
              className="ml-2 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-1.5"
              data-testid="wizard-save-exit"
            >
              <Save className="w-3.5 h-3.5" />
              Save & Exit
            </button>
          )}
        </div>
      </div>

      {/* ── 3-Panel Layout ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ═══ STEP SIDEBAR ═══ */}
        <div className="w-[260px] flex-shrink-0 bg-white border-r border-slate-200 overflow-y-auto" data-testid="wizard-step-sidebar">
          <div className="px-5 pt-5 pb-3">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Steps
            </h3>
          </div>
          <nav className="pb-4">
            {steps.map((step, idx) => {
              const isActive = idx === currentStep;
              const isDone = step.isComplete;
              return (
                <button
                  key={idx}
                  onClick={() => onStepChange?.(idx)}
                  className={`w-full flex items-start gap-3 px-5 py-3.5 text-left transition-colors border-l-[3px] ${
                    isActive
                      ? "bg-sky-50 border-l-sky-700"
                      : isDone
                      ? "hover:bg-slate-50 border-l-transparent opacity-70"
                      : "hover:bg-slate-50 border-l-transparent"
                  }`}
                  data-testid={`wizard-step-${idx}`}
                >
                  {/* Step Number */}
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      isDone
                        ? "bg-teal-100 text-teal-600"
                        : isActive
                        ? "bg-sky-700 text-white"
                        : "bg-slate-200 text-slate-500"
                    }`}
                  >
                    {isDone ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                  </div>
                  {/* Step Info */}
                  <div className="min-w-0">
                    <p className={`text-sm font-semibold ${isDone ? "line-through text-slate-400" : isActive ? "text-sky-700" : "text-slate-700"}`}>
                      {step.title}
                    </p>
                    {step.subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{step.subtitle}</p>}
                  </div>
                </button>
              );
            })}
          </nav>
        </div>

        {/* ═══ MAIN CONTENT ═══ */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="p-8 max-w-[760px]" data-testid="wizard-content">
              {children}
            </div>
          </div>

          {/* ── Bottom Action Bar ── */}
          <div className="flex items-center justify-between px-8 py-3.5 bg-white border-t border-slate-200 flex-shrink-0" data-testid="wizard-action-bar">
            <div className="flex gap-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-1.5"
                  data-testid="wizard-back-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}
            </div>
            <div className="flex gap-3">
              {onContinue && (
                <button
                  onClick={onContinue}
                  className="px-5 py-2.5 bg-sky-700 hover:bg-sky-800 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-1.5"
                  data-testid="wizard-continue-btn"
                >
                  {continueLabel}
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ═══ CONTEXT HELP PANEL ═══ */}
        {contextHelp && (
          <div
            className={`flex-shrink-0 bg-white border-l border-slate-200 overflow-y-auto transition-all duration-300 ${
              helpOpen ? "w-[260px]" : "w-0 overflow-hidden"
            }`}
            data-testid="wizard-context-panel"
          >
            {helpOpen && (
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-bold text-sky-700 flex items-center gap-1.5 uppercase tracking-wider" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    <Info className="w-3.5 h-3.5" />
                    {contextHelp.title || "Why does this matter?"}
                  </h3>
                  <button onClick={() => setHelpOpen(false)} className="text-slate-400 hover:text-slate-600 p-0.5">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                {contextHelp.sections?.map((section, i) => (
                  <div key={i} className="mb-5">
                    <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">{section.heading}</h4>
                    <p className="text-xs text-slate-600 leading-relaxed">{section.content}</p>
                  </div>
                ))}
                {contextHelp.isoClause && (
                  <div className="mt-4">
                    <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-[10px] font-semibold px-2.5 py-1 rounded">
                      <BookOpen className="w-3 h-3" />
                      {contextHelp.isoClause}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Help toggle when collapsed */}
        {contextHelp && !helpOpen && (
          <button
            onClick={() => setHelpOpen(true)}
            className="fixed right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-12 bg-white border border-slate-200 border-r-0 rounded-l-lg flex items-center justify-center text-slate-400 hover:text-sky-700 transition-colors"
            data-testid="wizard-help-toggle"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * ClinicalInsight — Inline tooltip for form fields
 * Shows ISO-aligned contextual help within the wizard content
 */
export function ClinicalInsight({ title, children }) {
  return (
    <div className="flex gap-2.5 p-3 bg-sky-50 border border-sky-200 rounded-lg mt-2 text-xs text-sky-800 leading-relaxed" data-testid="clinical-insight">
      <div className="w-5 h-5 rounded-full bg-sky-700 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">
        i
      </div>
      <div>
        {title && <strong className="block text-[10px] text-sky-700 uppercase tracking-wide mb-1">{title}</strong>}
        {children}
      </div>
    </div>
  );
}

/**
 * WhatsNextBanner — Shows after completing a wizard step
 */
export function WhatsNextBanner({ title, description, onNext, onDismiss }) {
  return (
    <div className="flex items-center gap-4 p-4 bg-teal-50 border border-teal-200 rounded-xl mt-6 animate-fade-in" data-testid="whats-next-banner">
      <div className="w-9 h-9 rounded-full bg-teal-500 text-white flex items-center justify-center flex-shrink-0">
        <Check className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-teal-800">{title}</p>
        {description && <p className="text-xs text-teal-600 mt-0.5">{description}</p>}
      </div>
      <div className="flex gap-2 flex-shrink-0">
        {onNext && (
          <button onClick={onNext} className="px-3.5 py-2 bg-sky-700 hover:bg-sky-800 text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1">
            Next Step <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
        {onDismiss && (
          <button onClick={onDismiss} className="px-3 py-2 text-xs font-medium text-slate-500 hover:bg-slate-100 rounded-lg transition-colors">
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}
